import { z } from 'zod'
import {
  graphEdgeSchema,
  graphNodeSchema,
  strategyScriptSchema,
  type GraphNode,
  type GraphEdge,
  type StrategyScript,
} from '@/features/backtest/schemas/backtest-types'
import { backfillInputSourcesFromEdges } from '@/features/backtest/utils/data-cleaning-utils'

export interface StrategyExportV1 {
  version: 1
  name: string
  description: string | null
  tags: string[]
  nodes: GraphNode[]
  edges: GraphEdge[]
  exportedAt: string // ISO date
}

const StrategyShareUiStateSchema = z.object({
  activePanes: z.array(z.string()).optional(),
  dividerPosition: z.number().optional(),
  editorMode: z.enum(['form', 'canvas', 'code']).optional(),
})

export const BacktestStrategyExportSchema = z.object({
  exportType: z.literal('backtest-strategy'),
  scope: z.literal('backtest'),
  version: z.literal(2),
  name: z.string().min(1),
  description: z.string().nullable(),
  tags: z.array(z.string()),
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  script: strategyScriptSchema.optional(),
  uiState: StrategyShareUiStateSchema.optional(),
  exportedAt: z.string(),
})

export type BacktestStrategyExport = z.infer<typeof BacktestStrategyExportSchema>

interface StrategySnapshotInput {
  name: string
  description?: string | null
  tags?: string[]
  nodes: GraphNode[]
  edges: GraphEdge[]
  script?: StrategyScript
  uiState?: z.infer<typeof StrategyShareUiStateSchema>
}

function buildStrategySnapshot(strategy: StrategySnapshotInput) {
  return {
    name: strategy.name,
    description: strategy.description ?? null,
    tags: strategy.tags ?? [],
    nodes: strategy.nodes.map((node) => cleanNodeForExport(node, strategy.nodes, strategy.edges)),
    edges: strategy.edges.map(cleanEdgeForExport),
  }
}

export function buildBacktestStrategyExport(
  strategy: StrategySnapshotInput,
): BacktestStrategyExport {
  return {
    exportType: 'backtest-strategy',
    scope: 'backtest',
    version: 2,
    ...buildStrategySnapshot(strategy),
    ...(strategy.script ? { script: strategy.script } : {}),
    ...(strategy.uiState ? { uiState: strategy.uiState } : {}),
    exportedAt: new Date().toISOString(),
  }
}

export function validateBacktestStrategyImport(data: unknown): BacktestStrategyExport | null {
  const result = BacktestStrategyExportSchema.safeParse(data)
  return result.success ? result.data : null
}

/**
 * Serialize a strategy for export as JSON.
 * Strips runtime-only fields (callbacks, connection state) from nodes.
 */
export function exportStrategy(strategy: {
  name: string
  description?: string | null
  tags?: string[]
  nodes: GraphNode[]
  edges: GraphEdge[]
}): string {
  const cleaned: StrategyExportV1 = {
    version: 1,
    ...buildStrategySnapshot(strategy),
    exportedAt: new Date().toISOString(),
  }
  return JSON.stringify(cleaned, null, 2)
}

/**
 * Parse and validate an imported strategy JSON string.
 * Returns the parsed data ready for createStrategy, or throws with a descriptive error.
 */
export function parseImportedStrategy(json: string): {
  name: string
  description: string | null
  tags: string[]
  nodes: GraphNode[]
  edges: GraphEdge[]
} {
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    throw new Error('Invalid JSON format')
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Import data must be a JSON object')
  }

  const data = parsed as Record<string, unknown>

  if (data.version !== 1) {
    throw new Error(`Unsupported export version: ${data.version ?? 'unknown'}`)
  }

  if (typeof data.name !== 'string' || !data.name.trim()) {
    throw new Error('Strategy name is required')
  }

  if (!Array.isArray(data.nodes)) {
    throw new Error('Strategy must contain a nodes array')
  }

  if (!Array.isArray(data.edges)) {
    throw new Error('Strategy must contain an edges array')
  }

  // Validate node structure minimally
  for (const node of data.nodes) {
    if (!node.id || !node.type || !node.position) {
      throw new Error('Each node must have id, type, and position')
    }
  }

  return {
    name: data.name,
    description: typeof data.description === 'string' ? data.description : null,
    tags: Array.isArray(data.tags)
      ? data.tags.filter((t): t is string => typeof t === 'string')
      : [],
    nodes: data.nodes as GraphNode[],
    edges: data.edges as GraphEdge[],
  }
}

/**
 * Trigger a browser file download with the given content.
 */
export function downloadStrategy(strategy: {
  name: string
  description?: string | null
  tags?: string[]
  nodes: GraphNode[]
  edges: GraphEdge[]
}): void {
  const json = exportStrategy(strategy)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${strategy.name
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()}-strategy.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function cleanNodeForExport(
  node: GraphNode,
  allNodes: GraphNode[],
  allEdges: GraphEdge[],
): GraphNode {
  const { data, ...rest } = node
  // Strip runtime callback fields from data
  const cleanData = { ...(data as Record<string, unknown>) }
  const runtimeKeys = [
    'onParameterChange',
    'onDuplicate',
    'onDelete',
    'onDetach',
    'onRemoveConnection',
    'getConnectedSources',
    '_connections',
    'validationError',
    'isHighlighted',
    'isFaded',
  ]
  for (const key of runtimeKeys) {
    delete cleanData[key]
  }
  // Heal node-inputs left with a null `source` (created via auto-connect /
  // wire-insert / paste / bypass, which don't sync it) by deriving it from the
  // incoming edge — same fix as cleanDataForSerialization, applied to this
  // separate file-export path. See data-cleaning-utils for the rationale.
  if (Array.isArray(cleanData.inputs)) {
    cleanData.inputs = backfillInputSourcesFromEdges(
      cleanData.inputs,
      node.id,
      allNodes as unknown as Array<Record<string, unknown>>,
      allEdges as unknown as Array<Record<string, unknown>>,
    ) as typeof cleanData.inputs
  }
  return { ...rest, data: cleanData }
}

function cleanEdgeForExport(edge: GraphEdge): GraphEdge {
  // Keep only the essential edge properties
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? undefined,
    targetHandle: edge.targetHandle ?? undefined,
  }
}
